import React, { useState, useMemo } from 'react'
import { useHRStore } from '../store/hrStore'
import { DollarSign, Calendar, TrendingUp, Users, Plus, Download, Send, CheckCircle } from 'lucide-react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function PayrollPage() {
  const { employees, payrollHistory, addPayroll, updatePayroll, getPayrollStats } = useHRStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [payrollForm, setPayrollForm] = useState({
    employeeId: '',
    gross: 0,
    taxes: 0,
    deductions: 0,
    net: 0,
    status: 'Pending',
  })

  const payrollStats = useMemo(() => getPayrollStats(), [getPayrollStats])

  // Get recent payroll
  const recentPayroll = useMemo(() => {
    return payrollHistory.slice(-10).reverse()
  }, [payrollHistory])

  // Generate monthly payroll chart data
  const monthlyPayrollData = useMemo(() => {
    const data = {}
    payrollHistory.forEach((p) => {
      const month = p.date.substring(0, 7)
      if (!data[month]) {
        data[month] = { month, total: 0, employees: 0 }
      }
      data[month].total += p.gross
      data[month].employees += 1
    })
    return Object.values(data).slice(-6)
  }, [payrollHistory])

  const handleCalculatePayroll = () => {
    const emp = employees.find((e) => e.id === parseInt(selectedEmployee))
    if (!emp) return

    const gross = emp.salary / 12
    const taxes = gross * 0.2
    const deductions = gross * 0.05
    const net = gross - taxes - deductions

    setPayrollForm({
      employeeId: parseInt(selectedEmployee),
      gross: parseFloat(gross.toFixed(2)),
      taxes: parseFloat(taxes.toFixed(2)),
      deductions: parseFloat(deductions.toFixed(2)),
      net: parseFloat(net.toFixed(2)),
      status: 'Pending',
    })
  }

  const handleSavePayroll = () => {
    if (!selectedEmployee || !payrollForm.gross) {
      alert('Please fill in all required fields')
      return
    }
    addPayroll(payrollForm)
    setShowAddModal(false)
    setSelectedEmployee('')
    setPayrollForm({
      employeeId: '',
      gross: 0,
      taxes: 0,
      deductions: 0,
      net: 0,
      status: 'Pending',
    })
  }

  const getEmployeeName = (empId) => {
    return employees.find((e) => e.id === empId)?.name || 'Unknown'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Payroll Management</h1>
          <p className="text-slate-400">Process payroll, manage taxes and deductions, generate payslips</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Gross Pay</h3>
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">${(payrollStats.totalGross / 1000).toFixed(1)}K</div>
            <div className="text-sm text-slate-400">This period</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6 hover:border-red-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Taxes</h3>
              <TrendingUp className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-3xl font-bold text-white">${(payrollStats.totalTaxes / 1000).toFixed(1)}K</div>
            <div className="text-sm text-slate-400">Withheld</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Net Pay</h3>
              <DollarSign className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">${(payrollStats.totalNet / 1000).toFixed(1)}K</div>
            <div className="text-sm text-slate-400">After deductions</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Processed</h3>
              <CheckCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{payrollHistory.filter((p) => p.status === 'Processed').length}</div>
            <div className="text-sm text-slate-400">Payroll runs</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Payroll */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <BarChart className="w-5 h-5 text-green-400" />
              Monthly Payroll
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyPayrollData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #10b981' }} />
                <Bar dataKey="total" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Employee Count */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Employees on Payroll
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyPayrollData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }} />
                <Line type="monotone" dataKey="employees" stroke="#0284c7" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payroll Records */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-indigo-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-6 h-6 text-indigo-400" />
              Payroll History
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Process Payroll
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Date</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Employee</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Gross</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Taxes</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Deductions</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Net</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentPayroll.map((record) => (
                  <tr key={record.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4 text-white">{record.date}</td>
                    <td className="py-3 px-4 text-blue-300">{getEmployeeName(record.employeeId)}</td>
                    <td className="py-3 px-4 text-right text-green-400 font-semibold">${record.gross.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-red-400 font-semibold">${record.taxes.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-yellow-400 font-semibold">${record.deductions.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-cyan-400 font-semibold">${record.net.toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        record.status === 'Processed' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Payroll Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl border border-indigo-500/20 max-w-md w-full mx-4 p-6 max-h-96 overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-4">Process Payroll</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Employee</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => {
                      setSelectedEmployee(e.target.value)
                      if (e.target.value) {
                        setPayrollForm({ ...payrollForm, employeeId: parseInt(e.target.value) })
                      }
                    }}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - ${(emp.salary / 12).toFixed(2)}/month
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleCalculatePayroll}
                  disabled={!selectedEmployee}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Auto-Calculate
                </button>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Gross Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={payrollForm.gross}
                    onChange={(e) => setPayrollForm({ ...payrollForm, gross: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Taxes</label>
                  <input
                    type="number"
                    step="0.01"
                    value={payrollForm.taxes}
                    onChange={(e) => setPayrollForm({ ...payrollForm, taxes: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Deductions</label>
                  <input
                    type="number"
                    step="0.01"
                    value={payrollForm.deductions}
                    onChange={(e) => setPayrollForm({ ...payrollForm, deductions: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Net Pay</label>
                  <input
                    type="number"
                    step="0.01"
                    value={payrollForm.net}
                    onChange={(e) => setPayrollForm({ ...payrollForm, net: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePayroll}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Process
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
